/**
 * Home's "Recent walks" (LED-1151): the scoped project's walks, or every
 * project's, two to a row.
 *
 * The tile on the left is a PLACEHOLDER in the walk's project tone. `listWalks`
 * deliberately projects no media URLs (a presigned GET per walk on a route the
 * Walks page calls with `limit=200` would be 200 presigns to decorate four
 * cards), so the card shows how many photos the walk took rather than one of
 * them. TODO(LED-1151): a bounded, opt-in cover-photo field on `/api/walks`.
 */

import { FONT_MONO, H } from "../../tokens";
import type { ConsoleWalkResponse } from "../../api";

function tileFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = (Math.imul(hash, 31) + id.charCodeAt(i)) | 0;
  const [a, b] = H.tiles[Math.abs(hash) % H.tiles.length]!;
  return `repeating-linear-gradient(135deg, ${a} 0, ${a} 8px, ${b} 8px, ${b} 16px)`;
}

function when(iso: string): string {
  const at = new Date(iso);
  const date = at.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const hours = at.getHours();
  const clock = `${hours % 12 || 12}:${String(at.getMinutes()).padStart(2, "0")}${hours < 12 ? "a" : "p"}`;
  return `${date} · ${clock}`;
}

function n(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`;
}

export function HomeWalks({
  walks,
  total,
  showProject,
  siteName,
  allHref,
  hrefForWalk,
  onOpenWalk,
  onNavigate,
  animClass,
}: {
  walks: ConsoleWalkResponse[];
  total: number;
  showProject: boolean;
  siteName: (id: string) => string;
  allHref: string;
  hrefForWalk: (id: string) => string;
  onOpenWalk: (id: string) => void;
  onNavigate: (href: string) => void;
  animClass: string;
}) {
  if (walks.length === 0) return null;
  return (
    <div data-testid="recent-walks" className={animClass}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginTop: 34 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: "-0.4px" }}>Recent walks</h2>
        <a
          href={allHref}
          className="kb hlink"
          data-testid="recent-walks-all"
          onClick={(event) => {
            event.preventDefault();
            onNavigate(allHref);
          }}
          style={{ fontSize: 13, fontWeight: 600, color: H.sub }}
        >
          {`All ${n(total, "walk", "walks")} ›`}
        </a>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, marginTop: 12 }}>
        {walks.slice(0, 4).map((walk) => {
          const toReview = walk.pendingReviewCount > 0;
          const empty = !toReview && walk.itemCount === 0;
          const statusInk = toReview ? H.amberInk : empty ? H.muted : H.greenInk;
          const statusDot = toReview ? H.amberDot : empty ? H.dotIdle : H.green;
          return (
            <a
              key={walk.id}
              href={hrefForWalk(walk.id)}
              className="kb hcard press"
              data-testid="recent-walk-card"
              onClick={(event) => {
                event.preventDefault();
                onOpenWalk(walk.id);
              }}
              style={{ display: "flex", gap: 14, padding: 14, borderRadius: 14, background: H.white, color: H.ink, minWidth: 0 }}
            >
              <span aria-hidden style={{ position: "relative", flex: "none", width: 72, height: 72, borderRadius: 10, overflow: "hidden", background: tileFor(walk.id) }} />
              <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "0.8px", color: H.muted, whiteSpace: "nowrap" }}>
                    {when(walk.startedAt)}
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: "1px", color: statusInk, whiteSpace: "nowrap" }}>
                    <span style={{ width: 6, height: 6, borderRadius: 3, background: statusDot }} />
                    {toReview ? "TO REVIEW" : walk.itemCount > 0 ? "REVIEWED" : "NO RECORDS"}
                  </span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.25px", marginTop: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {walk.name || "Untitled walk"}
                </div>
                <div style={{ flex: 1 }} />
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: H.sub, whiteSpace: "nowrap", overflow: "hidden" }}>
                  {showProject && (
                    <>
                      <span style={{ fontWeight: 600, color: H.text, overflow: "hidden", textOverflow: "ellipsis" }}>{siteName(walk.jobSiteId)}</span>
                      <span style={{ color: H.dim }}>·</span>
                    </>
                  )}
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {[
                      walk.recordedMinutes === undefined ? null : `${walk.recordedMinutes} min`,
                      n(walk.itemCount, "record", "records"),
                      walk.photoCount ? n(walk.photoCount, "photo", "photos") : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
